import { Backdrop, CircularProgress } from '@mui/material';
import { routes } from 'api';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { APP_URLS } from '../config/api-urls';
import { AppRoutes } from '../config/app-routes';
import { CognitoUser, Profile } from '../types';
import { useAxios } from '../utils/transport/useAxios';

interface AuthContextType {
  user?: CognitoUser;
  loading: boolean;
  error?: any;
  setError: (value: any) => void;
  setUserToApp: (user: CognitoUser) => void;
  userProfile?: Profile;
  setUserProfile: (profile: Profile) => void;
}

// interval id for periodic validation
function startPeriodicUserValidation() {
  if (!window.userValidationInterval) {
    window.lastCheck = Date.now();
    const checkPeriod = 1000 * 5; // run check every 5 seconds
    const validatePeriod = 1000 * 60; // only call API every minute

    window.userValidationInterval = setInterval(() => {
      if (Date.now() - window.lastCheck! > validatePeriod) {
        window.lastCheck = Date.now();
        if (window.AxiosInstance) {
          // perform check
          window.AxiosInstance.get(routes.isLoggedIn(), { withCredentials: true });
        }
      }
    }, checkPeriod);
  }
}

function stopPeriodicUserValidation() {
  if (window.userValidationInterval) {
    clearInterval(window.userValidationInterval);
    window.userValidationInterval = undefined;
  }
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

/**
 * Export the provider as we need to wrap the entire app with it
 * @function
 * @name AuthProvider
 * @param {object} children - child components
 * @return {functions} state managment and html event handler
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<CognitoUser>();
  const [error, setError] = useState<any>();
  const [loading] = useState<boolean>(false);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
  const [userProfile, setUserProfile] = useState<Profile>();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const axios = useAxios();
  const authRoutes = ['/404', '/activate-user'];

  useEffect(() => {
    if (user) {
      startPeriodicUserValidation();
    } else {
      stopPeriodicUserValidation();
    }
    return () => {
      stopPeriodicUserValidation();
    };
  }, [user]);

  const redirectUrl = useMemo(() => {
    const redirect = searchParams.get('redirect');
    let url: string = AppRoutes.DASHBOARD;
    if (redirect && !`/${redirect}`.startsWith(AppRoutes.LOGIN)) {
      try {
        new URL(redirect);
      } catch (e) {
        // expect it to fail because it should be a relative url.
        url = redirect;
      }
    }
    return url;
  }, [searchParams]);

  /**
   * set local storage for user data (login)
   * @function
   * @name setUserToApp
   * @param {CognitoUser} data - cognito user data
   * @return {void}
   */
  const setUserToApp = (data: CognitoUser) => {
    setUser(data);
    navigate(redirectUrl);
  };

  useEffect(() => {
    setError(undefined);

    // If not on a public route, check authentication. The consent dashboard serving this
    // app has already signed the user in; if the API rejects the session, the 401 handler
    // hands sign-in back to it. (Don't check for an auth cookie here: the access token
    // cookies are HTTP-only, and Kinde sessions have no `access-token-expiry` cookie.)
    if (authRoutes.indexOf(location.pathname) === -1) {
      axios
        .get<CognitoUser>(APP_URLS.currentUser(), { withCredentials: true })
        .then(({ data: user }) => {
          setUser(user);
          // If on a public route after login, redirect to intended page
          if (authRoutes.indexOf(location.pathname) > -1) {
            navigate(redirectUrl);
          }
        })
        .catch((_) => {
          // eslint-disable-next-line no-console
          console.warn('Failed to fetch current user:', _);
        })
        .finally(() => setLoadingInitial(false));
    } else {
      setLoadingInitial(false);
    }
    // eslint-disable-next-line
  }, [axios]);

  if (loadingInitial) {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loadingInitial}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  // We only want to render the underlying app after we
  // assert for the presence of a current user.
  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        setUserProfile,
        setUserToApp,
        loading,
        error,
        setError,
      }}
    >
      <React.Suspense>{children}</React.Suspense>
    </AuthContext.Provider>
  );
}

// Let's only export the `useAuth` hook instead of the context.
// We only want to use the hook directly and never the context component.
export default function useAuth() {
  return useContext(AuthContext);
}
